import React, { useState, useEffect } from 'react';
import { X, Minimize2 } from 'lucide-react';

const fullText = `
Welcome to Paradise, an escape from reality

> INSTRUCTIONS
  1. Create an account to subscribe
  2. Access full content (Images, Newsroom, PDFs)

> NAVIGATION GUIDE
  [♪] Music Player: Listen to tunes (Bottom Dock)
  [📄] PDFs: Browse and view documents
  [🖼] Images: Subscriber-only gallery
  [📰] Newsroom: Latest updates
  [👥] Members: Access your area
  [📸] Instagram: Browse the feed
  [⚙] Settings: Customize themes

Enjoy your trip to paradise.
`;

const WelcomeScreen = ({ onClose, onMinimize, isMobile }) => {
    const [text, setText] = useState('');
    const [isTypingComplete, setIsTypingComplete] = useState(false);

    // Typewriter Effect
    useEffect(() => {
        let currentIndex = 0;
        const typingSpeed = 20; // ms per character

        const typeChar = () => {
            if (currentIndex < fullText.length) {
                setText(fullText.slice(0, currentIndex + 1));
                currentIndex++;
                setTimeout(typeChar, typingSpeed);
            } else {
                setIsTypingComplete(true);
            }
        };

        // Start typing immediately
        const startTypingTimer = setTimeout(() => {
            typeChar();
        }, 300);

        return () => clearTimeout(startTypingTimer);
    }, []);

    return (
        <div className="flex flex-col h-full w-full bg-[#f4f4f4] text-[#333333] font-mono overflow-hidden relative border border-[#d1d1d1] shadow-2xl">
            {/* Header / Title Bar - Braun Style */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#e8e6df] border-b border-[#d1d1d1]">
                <div className="flex items-center gap-2 text-[#444444]">
                    <div className="w-3 h-3 rounded-full bg-[#AF2E1B]"></div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase">System Info</span>
                </div>
                <div className="flex items-center gap-3">
                    {onMinimize && (
                        <button
                            onClick={onMinimize}
                            className="text-[#666666] hover:text-black transition-colors"
                            title="Minimize"
                        >
                            <Minimize2 size={18} strokeWidth={1.5} />
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="text-[#666666] hover:text-[#AF2E1B] transition-colors"
                        title="Close"
                    >
                        <X size={18} strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto p-8 custom-scrollbar bg-[#f4f4f4] relative">
                {/* CRT Scanline Overlay */}
                <div className="absolute inset-0 z-10 pointer-events-none opacity-[0.03] bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,1)_50%)] bg-[length:100%_4px]"></div>
                <div className="max-w-2xl mx-auto relative z-20">

                    {/* Typewriter Text */}
                    <div className="whitespace-pre-wrap leading-relaxed text-sm sm:text-base font-medium text-[#444444]">
                        <span>{text}</span>
                        {!isTypingComplete && (
                            <span className="inline-block w-2.5 h-5 bg-[#AF2E1B] ml-1 animate-pulse align-middle"></span>
                        )}
                    </div>

                    {/* Action Button (Appears after typing) - Braun Style */}
                    <div className={`mt-16 text-center transition-opacity duration-700 ${isTypingComplete ? 'opacity-100' : 'opacity-0'}`}>
                        <button
                            onClick={onClose}
                            className="group relative inline-flex items-center justify-center px-8 py-3 overflow-hidden transition-all duration-300 bg-[#e8e6df] border border-[#d1d1d1] rounded hover:bg-[#dcdad3] active:scale-95 shadow-sm hover:shadow-md"
                        >
                            <span className="font-bold tracking-widest text-xs uppercase text-[#333333] group-hover:text-black">
                                Initialize System
                            </span>
                            <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-[#AF2E1B] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </button>
                    </div>
                    </div>
                </div>
            {/* Footer Status Bar */}
            <div className="px-6 py-2 bg-[#e8e6df] border-t border-[#d1d1d1] text-[9px] font-bold tracking-widest text-[#666666] flex justify-between uppercase">
                <span>Paradise OS v2.0</span>
                <span>Ready</span>
            </div>
        </div>
    );
};

export default WelcomeScreen;
